import { msg } from "../../locales/translate";
import { TFraudRule } from "./types";

/*eslint-disable @typescript-eslint/no-explicit-any*/
interface Rule {
	field: string;
	[key: string]: any;
}

export function dehydrate(data: any): any {
	if (Array.isArray(data)) {
		return data.map(item => dehydrate(item)).filter(Boolean);
	}

	if (data !== null && typeof data === "object") {
		const cleaned: any = {};

		for (const key in data) {
			// Skip meta fields (starting with $__)
			if (key.startsWith("$__")) {
				continue;
			}

			const value = data[key];

			// Skip empty values
			if (value === "" || value === null || value === undefined) {
				continue;
			}

			// Special handling for rules
			if (key === "rules" && Array.isArray(value)) {
				cleaned[key] = value
					.map((rule: Rule) => {
						const cleanedRule: any = {};
						const selectedField = rule.field;

						// Add field property
						if (selectedField && selectedField !== "Select field for the Rule") {
							cleanedRule.field = selectedField;

							// Add only fields related to the selected field type
							const operatorKey = `${selectedField}_operator`;

							if (rule[selectedField] !== "" && rule[selectedField] !== undefined) {
								cleanedRule[selectedField] = rule[selectedField];
							}

							if (rule[operatorKey] !== "" && rule[operatorKey] !== undefined) {
								cleanedRule[operatorKey] = rule[operatorKey];
							}
						}

						return Object.keys(cleanedRule).length > 1 ? cleanedRule : null;
					})
					.filter(Boolean);

				if (cleaned[key].length === 0) {
					delete cleaned[key];
				}
				continue;
			}

			// Special handling for threshold (simple only; enabled + percent_base)
			if (key === "threshold") {
				const thresholdCleaned: any = {};

				if (value.enabled !== undefined && value.enabled !== "") {
					thresholdCleaned.enabled = value.enabled;
				}

				const simpleBlock = value.simple;
				if (simpleBlock && typeof simpleBlock === "object") {
					const blockCleaned: any = {};
					for (const blockKey in simpleBlock) {
						if (blockKey.startsWith("plain_") || blockKey.startsWith("$__")) continue;
						const blockValue = simpleBlock[blockKey];
						if (blockValue === "" || blockValue === null || blockValue === undefined) continue;
						// percent_base is only meaningful when units === "%"
						if (blockKey === "percent_base" && simpleBlock.units !== "%") continue;
						blockCleaned[blockKey] = dehydrate(blockValue);
					}
					if (Object.keys(blockCleaned).length > 0) {
						thresholdCleaned.simple = blockCleaned;
					}
				}

				if (Object.keys(thresholdCleaned).length > 0) {
					cleaned[key] = thresholdCleaned;
				}

				continue;
			}

			// Recursively clean nested objects
			const cleanedValue = dehydrate(value);

			// Add only non-empty values
			if (cleanedValue !== null && cleanedValue !== undefined) {
				if (typeof cleanedValue === "object" && !Array.isArray(cleanedValue)) {
					// Object
					if (Object.keys(cleanedValue).length > 0) {
						cleaned[key] = cleanedValue;
					}
				} else if (Array.isArray(cleanedValue)) {
					// Array
					if (cleanedValue.length > 0) {
						cleaned[key] = cleanedValue;
					}
				} else {
					// Value
					cleaned[key] = cleanedValue;
				}
			}
		}

		return cleaned;
	}

	return data;
}

export function hydrate(data: Record<string, unknown>): TFraudRule["data"] {
	const result: any = structuredClone(template);

	function merge(target: any, source: any): void {
		// If target is not an object, do nothing
		if (typeof target !== "object" || target === null) {
			return;
		}

		for (const key in source) {
			const sourceValue = source[key];

			// Skip undefined and null
			if (sourceValue === undefined || sourceValue === null) {
				continue;
			}

			// If key is not in target, skip (don't add data that's not in template)
			if (!(key in target)) {
				continue;
			}

			// Handle arrays (rules)
			if (Array.isArray(sourceValue) && Array.isArray(target[key])) {
				const originalTarget = target[key];
				const firstItem = originalTarget[0];

				// For arrays of objects (e.g. rules), clone template per item and merge
				if (firstItem !== null && typeof firstItem === "object") {
					target[key] = sourceValue.map((item: any, i: number) => {
						// Use index-matched template element when available (e.g. blocks),
						// otherwise fall back to first element (e.g. rules with 1 template item)
						const templateItem = originalTarget[i] ?? originalTarget[0];
						const newRule = structuredClone(templateItem);
						merge(newRule, item);
						return newRule;
					});
				} else {
					// Primitive arrays (e.g. order: ["name", "groups"]) — assign directly
					target[key] = structuredClone(sourceValue);
				}
				continue;
			}

			// Handling threshold - special case (simple only)
			if (key === "threshold" && typeof sourceValue === "object" && typeof target[key] === "object") {
				if (sourceValue.enabled !== undefined) {
					target[key].enabled = sourceValue.enabled;
				}
				if (sourceValue.simple && typeof target[key].simple === "object") {
					merge(target[key].simple, sourceValue.simple);
				}
				continue;
			}

			// Handle objects
			if (typeof sourceValue === "object" && !Array.isArray(sourceValue) && typeof target[key] === "object" && !Array.isArray(target[key])) {
				merge(target[key], sourceValue);
				continue;
			}

			// Simple values
			target[key] = sourceValue;
		}
	}

	merge(result, data);
	return result;
}

export const template: TFraudRule["data"] = {
	main: {
		name: "",
		get $__name_placeholder() { return msg("ruleName"); },
		$__name_required: true,
		groups: "",
		get $__groups_placeholder() { return msg("selectGroups"); },
		$__groups_type: "select",
		$__groups_values: [
			{
				get name() { return msg("group1"); },
				value: "group1",
			},
			{
				get name() { return msg("group2"); },
				value: "group2",
			},
		],
		$__groups_required: true,
		action: "alert",
		get $__action_placeholder() { return msg("action"); },
		$__action_type: "select",
		$__action_values: [
			{
				get name() { return msg("alert"); },
				value: "alert",
			},
			{
				get name() { return msg("prevent"); },
				value: "prevent",
			},
		],
		$__action_required: true,
		status: "disabled",
		get $__status_placeholder() { return msg("ruleStatus"); },
		$__status_type: "select",
		$__status_values: [
			{
				get name() { return msg("active"); },
				value: "active",
			},
			{
				get name() { return msg("disabled"); },
				value: "disabled",
			},
		],
	},
	$__main_decorator: {
		get title() { return msg("mainBlockOfTheRule"); },
		blocks: [
			{
				blockless: true,
				order: ["name", "groups"],
				gridTemplateColumns: "2fr 1fr",
			},
			{
				blockless: true,
				order: ["action", "status"],
				gridTemplateColumns: "1fr 1fr 2fr",
			},
		],
	},
	threshold: {
		enabled: "off",
		get $__enabled_label() { return msg("threshold"); },
		$__enabled_type: "radio",
		$__enabled_values: [
			{
				get name() { return msg("on"); },
				value: "on",
			},
			{
				get name() { return msg("off"); },
				value: "off",
			},
		],
		space: "20px",
		$__space_type: "spacer",
		simple: {
			plain_if: "IF",
			$__plain_if_type: "plain",
			$__plain_if_sx: {
				placeSelf: "center",
				marginTop: "16px",
			},
			simple_operator: "",
			get $__simple_operator_label() { return msg("operator"); },
			get $__simple_operator_placeholder() { return msg("operator"); },
			$__simple_operator_type: "select",
			$__simple_operator_values: [
				{
					name: "=",
					value: "eq",
				},
				{
					name: "!=",
					value: "neq",
				},
				{
					name: ">",
					value: "gt",
				},
				{
					name: "<",
					value: "lt",
				},
				{
					name: ">=",
					value: "gte",
				},
				{
					name: "<=",
					value: "lte",
				},
			],
			$__simple_operator_linked: "",
			$__simple_operator_linked_value: "",
			$__simple_operator_optional: true,
			value: "",
			get $__value_label() { return msg("value"); },
			$__value_type: "number",
			units: "%",
			get $__units_label() { return msg("units"); },
			$__units_type: "select",
			$__units_values: [
				{
					name: "%",
					value: "%",
				},
				{
					get name() { return msg("items"); },
					value: "items",
				},
			],
			plain_of: "OF",
			$__plain_of_type: "plain",
			$__plain_of_sx: {
				placeSelf: "center",
				marginTop: "16px",
			},
			$__plain_of_linked: "units",
			$__plain_of_linked_value: "%",
			percent_base: "all",
			get $__percent_base_label() { return msg("base"); },
			$__percent_base_type: "select",
			$__percent_base_values: [
				{ get name() { return msg("allTransactions"); }, value: "all" },
				{ get name() { return msg("transfer"); }, value: "transfer" },
				{ get name() { return msg("refund"); }, value: "refund" },
				{ get name() { return msg("oct"); }, value: "oct" },
				{ get name() { return msg("chargeback"); }, value: "chargeback" },
				{ get name() { return msg("transferRefund"); }, value: "transfer,refund" },
				{ get name() { return msg("transferOct"); }, value: "transfer,oct" },
				{ get name() { return msg("transferChargeback"); }, value: "transfer,chargeback" },
				{ get name() { return msg("refundOct"); }, value: "refund,oct" },
				{ get name() { return msg("refundChargeback"); }, value: "refund,chargeback" },
				{ get name() { return msg("octChargeback"); }, value: "oct,chargeback" },
				{ get name() { return msg("transferRefundOct"); }, value: "transfer,refund,oct" },
				{ get name() { return msg("transferRefundChargeback"); }, value: "transfer,refund,chargeback" },
				{ get name() { return msg("transferOctChargeback"); }, value: "transfer,oct,chargeback" },
				{ get name() { return msg("refundOctChargeback"); }, value: "refund,oct,chargeback" },
				{ get name() { return msg("transferRefundOctChargeback"); }, value: "transfer,refund,oct,chargeback" },
			],
			$__percent_base_linked: "units",
			$__percent_base_linked_value: "%",
			$__percent_base_optional: true,
			plain_per: "PER",
			$__plain_per_type: "plain",
			$__plain_per_sx: {
				placeSelf: "center",
				marginTop: "16px",
			},
			timeframe: "",
			get $__timeframe_label() { return msg("timeframe"); },
			$__timeframe_type: "number",
			period: "minute",
			$__period_type: "select",
			$__period_values: [
				{
					get name() { return msg("minute"); },
					value: "minute",
				},
				{
					get name() { return msg("hour"); },
					value: "hour",
				},
				{
					get name() { return msg("day"); },
					value: "day",
				},
				{
					get name() { return msg("week"); },
					value: "week",
				},
				{
					get name() { return msg("month"); },
					value: "month",
				},
			],
		},
		$__simple_decorator: {
			blockless: true,
			blocks: [
				{
					blockless: true,
					order: ["plain_if", "simple_operator", "value", "units", "plain_per", "timeframe", "period"],
					gridTemplateColumns: "1fr 4fr 3fr 4fr 1fr 3fr 4fr",
				},
				{
					blockless: true,
					order: ["plain_of", "percent_base"],
					gridTemplateColumns: "1fr 7fr",
				},
			],
		},
		$__simple_linked: "enabled",
		$__simple_linked_value: "on",
		$__simple_optional: true,
	},
	$__threshold_decorator: {
		get title() { return msg("threshold"); },
		blocks: [
			{
				blockless: true,
				order: ["enabled"],
			},
			{
				blockless: true,
				order: ["space"],
			},
			{
				blockless: true,
				order: ["simple"],
			},
		],
	},
	combinator: {
		logical_operator: "and",
		$__logical_operator_label: "",
		get $__logical_operator_placeholder() { return msg("condition"); },
		$__logical_operator_type: "select",
		$__logical_operator_values: [
			{
				get name() { return msg("matchAllAnd"); },
				value: "and",
			},
			{
				get name() { return msg("matchAnyOr"); },
				value: "or",
			},
		],
		$__logical_operator_required: true,
	},
	$__combinator_decorator: {
		get title() { return msg("combinator"); },
		get subtitle() { return msg("specifyWhetherAllConditionsMustBeMetAndOrAtLeastOneOrForTheRuleToTrigger"); },
		blocks: [
			{
				blockless: true,
				order: ["logical_operator"],
				gridTemplateColumns: "2fr 3fr",
			},
		],
	},
	rules: [
		{
			field: "Select field for the Rule",
			$__field_type: "select",
			// Only fields supported by the evaluator are shown; the rest are hidden (kept for later).
			$__field_values: [
				{
					get name() { return msg("binCountry"); },
					value: "bin_country",
				},
				// {
				// 	name: "Card type",
				// 	value: "card_type",
				// },
				// {
				// 	name: "Founding method",
				// 	value: "founding_method",
				// },
				{
					get name() { return msg("currency"); },
					value: "currency",
				},
				// {
				// 	name: "Respond code",
				// 	value: "respond_code",
				// },
				// {
				// 	name: "Transaction type",
				// 	value: "transaction_type",
				// },
				// {
				// 	name: "Acquirer ID",
				// 	value: "acquirer_id",
				// },
				// {
				// 	name: "Merchant ID",
				// 	value: "merchant_id",
				// },
				// {
				// 	name: "Date",
				// 	value: "date",
				// },
				// {
				// 	name: "Time",
				// 	value: "time",
				// },
				// {
				// 	name: "Period",
				// 	value: "period",
				// },
				// {
				// 	name: "Card entry mode",
				// 	value: "card_entry_mode",
				// },
				// {
				// 	name: "Pan",
				// 	value: "pan",
				// },
				{
					get name() { return msg("authorisationAmount"); },
					value: "authorisation_amount",
				},
				// {
				// 	name: "Authorisation currency",
				// 	value: "authorisation_currency",
				// },
				// {
				// 	name: "Authorisation ID",
				// 	value: "authorisation_id",
				// },
				// {
				// 	name: "Chargeback request count",
				// 	value: "chargeback_request_count",
				// },
			],

			// BIN country
			bin_country: "",
			$__bin_country_type: "select",
			get $__bin_country_placeholder() { return msg("binCountry"); },
			$__bin_country_values: [
				{
					get name() { return msg("unitedStates"); },
					value: "US",
				},
				{
					get name() { return msg("unitedKingdom"); },
					value: "GB",
				},
				{
					get name() { return msg("germany"); },
					value: "DE",
				},
				{
					get name() { return msg("france"); },
					value: "FR",
				},
				{
					get name() { return msg("italy"); },
					value: "IT",
				},
				{
					get name() { return msg("spain"); },
					value: "ES",
				},
				{
					get name() { return msg("ukraine"); },
					value: "UA",
				},
				{
					get name() { return msg("latvia"); },
					value: "LV",
				},
			],
			$__bin_country_linked: "field",
			$__bin_country_linked_value: "bin_country",
			$__bin_country_optional: true,
			bin_country_operator: "",
			get $__bin_country_operator_label() { return msg("operator"); },
			get $__bin_country_operator_placeholder() { return msg("operator"); },
			$__bin_country_operator_type: "select",
			$__bin_country_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__bin_country_operator_linked: "field",
			$__bin_country_operator_linked_value: "bin_country",
			$__bin_country_operator_optional: true,

			// Card type: Consumer / Business
			card_type: "",
			$__card_type_type: "select",
			get $__card_type_placeholder() { return msg("cardType"); },
			$__card_type_values: [
				{
					get name() { return msg("consumer"); },
					value: "consumer",
				},
				{
					get name() { return msg("business"); },
					value: "business",
				},
			],
			$__card_type_linked: "field",
			$__card_type_linked_value: "card_type",
			$__card_type_optional: true,
			card_type_operator: "",
			get $__card_type_operator_label() { return msg("operator"); },
			get $__card_type_operator_placeholder() { return msg("operator"); },
			$__card_type_operator_type: "select",
			$__card_type_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__card_type_operator_linked: "field",
			$__card_type_operator_linked_value: "card_type",
			$__card_type_operator_optional: true,

			// Founding method: Debit / Credit / Prepaid / Prepaid Reloadable / Prepaid Non-Relaadable
			founding_method: "",
			$__founding_method_type: "select",
			get $__founding_method_placeholder() { return msg("foundingMethod"); },
			$__founding_method_values: [
				{
					get name() { return msg("debit"); },
					value: "debit",
				},
				{
					get name() { return msg("credit"); },
					value: "credit",
				},
				{
					get name() { return msg("prepaid"); },
					value: "prepaid",
				},
				{
					get name() { return msg("prepaidReloadable"); },
					value: "prepaid_reloadable",
				},
				{
					get name() { return msg("prepaidNonReloadable"); },
					value: "prepaid_non_reloadable",
				},
			],
			$__founding_method_linked: "field",
			$__founding_method_linked_value: "founding_method",
			$__founding_method_optional: true,
			founding_method_operator: "",
			get $__founding_method_operator_label() { return msg("operator"); },
			get $__founding_method_operator_placeholder() { return msg("operator"); },
			$__founding_method_operator_type: "select",
			$__founding_method_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__founding_method_operator_linked: "field",
			$__founding_method_operator_linked_value: "founding_method",
			$__founding_method_operator_optional: true,

			// Currency
			currency: "",
			$__currency_type: "select",
			get $__currency_placeholder() { return msg("currency"); },
			$__currency_values: [
				{
					name: "EUR",
					value: "EUR",
				},
				{
					name: "USD",
					value: "USD",
				},
			],
			$__currency_linked: "field",
			$__currency_linked_value: "currency",
			$__currency_optional: true,
			currency_operator: "",
			get $__currency_operator_label() { return msg("operator"); },
			get $__currency_operator_placeholder() { return msg("operator"); },
			$__currency_operator_type: "select",
			$__currency_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__currency_operator_linked: "field",
			$__currency_operator_linked_value: "currency",
			$__currency_operator_optional: true,

			// Respond code
			respond_code: "",
			$__respond_code_type: "select",
			get $__respond_code_placeholder() { return msg("respondCode"); },
			$__respond_code_values: [
				{
					get name() { return msg("ok"); },
					value: "200",
				},
				{
					get name() { return msg("error"); },
					value: "400",
				},
				{
					get name() { return msg("notFound"); },
					value: "404",
				},
				{
					get name() { return msg("requestTimeout"); },
					value: "408",
				},
				{
					get name() { return msg("serverError"); },
					value: "500",
				},
				{
					get name() { return msg("badGateway"); },
					value: "502",
				},
				{
					get name() { return msg("serviceUnavailable"); },
					value: "503",
				},
				{
					get name() { return msg("gatewayTimeout"); },
					value: "504",
				},
				{
					get name() { return msg("iAmATeapot"); },
					value: "418",
				},
			],
			$__respond_code_linked: "field",
			$__respond_code_linked_value: "respond_code",
			$__respond_code_optional: true,
			respond_code_operator: "",
			get $__respond_code_operator_label() { return msg("operator"); },
			get $__respond_code_operator_placeholder() { return msg("operator"); },
			$__respond_code_operator_type: "select",
			$__respond_code_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__respond_code_operator_linked: "field",
			$__respond_code_operator_linked_value: "respond_code",
			$__respond_code_operator_optional: true,

			// Transaction type
			transaction_type: "",
			$__transaction_type_type: "select",
			get $__transaction_type_placeholder() { return msg("transactionType"); },
			$__transaction_type_values: [
				{
					get name() { return msg("authorisation"); },
					value: "authorisation",
				},
				{
					get name() { return msg("transaction"); },
					value: "transaction",
				},
				{
					get name() { return msg("refund"); },
					value: "refund",
				},
			],
			$__transaction_type_linked: "field",
			$__transaction_type_linked_value: "transaction_type",
			$__transaction_type_optional: true,
			transaction_type_operator: "",
			get $__transaction_type_operator_label() { return msg("operator"); },
			get $__transaction_type_operator_placeholder() { return msg("operator"); },
			$__transaction_type_operator_type: "select",
			$__transaction_type_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__transaction_type_operator_linked: "field",
			$__transaction_type_operator_linked_value: "transaction_type",
			$__transaction_type_operator_optional: true,

			// Acquirer ID
			$__acquirer_id_linked: "field",
			$__acquirer_id_linked_value: "acquirer_id",
			$__acquirer_id_optional: true,
			acquirer_id_operator: "same",
			get $__acquirer_id_operator_label() { return msg("operator"); },
			get $__acquirer_id_operator_placeholder() { return msg("operator"); },
			$__acquirer_id_operator_type: "select",
			$__acquirer_id_operator_values: [
				{
					get name() { return msg("same"); },
					value: "same",
				},
				{
					get name() { return msg("notSame"); },
					value: "not_same",
				},
				{
					get name() { return msg("forEach"); },
					value: "each",
				},
			],
			$__acquirer_id_operator_linked: "field",
			$__acquirer_id_operator_linked_value: "acquirer_id",
			$__acquirer_id_operator_optional: true,

			// Merchant ID
			$__merchant_id_linked: "field",
			$__merchant_id_linked_value: "merchant_id",
			$__merchant_id_optional: true,
			merchant_id_operator: "same",
			get $__merchant_id_operator_label() { return msg("operator"); },
			get $__merchant_id_operator_placeholder() { return msg("operator"); },
			$__merchant_id_operator_type: "select",
			$__merchant_id_operator_values: [
				{
					get name() { return msg("same"); },
					value: "same",
				},
				{
					get name() { return msg("notSame"); },
					value: "not_same",
				},
				{
					get name() { return msg("forEach"); },
					value: "each",
				},
			],
			$__merchant_id_operator_linked: "field",
			$__merchant_id_operator_linked_value: "merchant_id",
			$__merchant_id_operator_optional: true,

			// Date
			date: "",
			$__date_type: "date",
			get $__date_placeholder() { return msg("date_99c40ab4"); },
			$__date_linked: "field",
			$__date_linked_value: "date",
			$__date_optional: true,
			date_operator: "",
			get $__date_operator_label() { return msg("operator"); },
			get $__date_operator_placeholder() { return msg("operator"); },
			$__date_operator_type: "select",
			$__date_operator_values: [
				{
					name: "=",
					value: "eq",
				},
				{
					name: "!=",
					value: "neq",
				},
				{
					name: ">",
					value: "gt",
				},
				{
					name: "<",
					value: "lt",
				},
				{
					name: ">=",
					value: "gte",
				},
				{
					name: "<=",
					value: "lte",
				},
			],
			$__date_operator_linked: "field",
			$__date_operator_linked_value: "date",
			$__date_operator_optional: true,

			// Time
			time: "",
			$__time_type: "time",
			get $__time_placeholder() { return msg("time"); },
			$__time_linked: "field",
			$__time_linked_value: "time",
			$__time_optional: true,
			time_operator: "",
			get $__time_operator_label() { return msg("operator"); },
			get $__time_operator_placeholder() { return msg("operator"); },
			$__time_operator_type: "select",
			$__time_operator_values: [
				{
					name: "=",
					value: "eq",
				},
				{
					name: "!=",
					value: "neq",
				},
				{
					name: ">",
					value: "gt",
				},
				{
					name: "<",
					value: "lt",
				},
				{
					name: ">=",
					value: "gte",
				},
				{
					name: "<=",
					value: "lte",
				},
			],
			$__time_operator_linked: "field",
			$__time_operator_linked_value: "time",
			$__time_operator_optional: true,

			// Period
			period: "",
			$__period_type: "period",
			get $__period_placeholder() { return msg("period_6e795d4d"); },
			$__period_linked: "field",
			$__period_linked_value: "period",
			$__period_optional: true,
			period_operator: "",
			get $__period_operator_label() { return msg("operator"); },
			get $__period_operator_placeholder() { return msg("operator"); },
			$__period_operator_type: "select",
			$__period_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__period_operator_linked: "field",
			$__period_operator_linked_value: "period",
			$__period_operator_optional: true,

			// Card entry mode
			card_entry_mode: "",
			$__card_entry_mode_type: "select",
			get $__card_entry_mode_placeholder() { return msg("cardEntryMode"); },
			$__card_entry_mode_values: [
				{
					get name() { return msg("motoManualEntry"); },
					value: "moto",
				},
				{
					get name() { return msg("tokenised"); },
					value: "tokenised",
				},
				{
					get name() { return msg("cardOnFileStoredCredentials"); },
					value: "card_on_file",
				},
			],
			$__card_entry_mode_linked: "field",
			$__card_entry_mode_linked_value: "card_entry_mode",
			$__card_entry_mode_optional: true,
			card_entry_mode_operator: "",
			get $__card_entry_mode_operator_label() { return msg("operator"); },
			get $__card_entry_mode_operator_placeholder() { return msg("operator"); },
			$__card_entry_mode_operator_type: "select",
			$__card_entry_mode_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__card_entry_mode_operator_linked: "field",
			$__card_entry_mode_operator_linked_value: "card_entry_mode",
			$__card_entry_mode_operator_optional: true,

			// PAN
			$__pan_linked: "field",
			$__pan_linked_value: "pan",
			$__pan_optional: true,
			pan_operator: "same",
			get $__pan_operator_label() { return msg("operator"); },
			get $__pan_operator_placeholder() { return msg("operator"); },
			$__pan_operator_type: "select",
			$__pan_operator_values: [
				{
					get name() { return msg("same"); },
					value: "same",
				},
				{
					get name() { return msg("notSame"); },
					value: "not_same",
				},
				{
					get name() { return msg("forEach"); },
					value: "each",
				},
			],
			$__pan_operator_linked: "field",
			$__pan_operator_linked_value: "pan",
			$__pan_operator_optional: true,

			// Authorisation amount
			authorisation_amount: "",
			$__authorisation_amount_type: "number",
			get $__authorisation_amount_placeholder() { return msg("authorisationAmount"); },
			$__authorisation_amount_linked: "field",
			$__authorisation_amount_linked_value: "authorisation_amount",
			$__authorisation_amount_optional: true,
			authorisation_amount_operator: "",
			get $__authorisation_amount_operator_label() { return msg("operator"); },
			get $__authorisation_amount_operator_placeholder() { return msg("operator"); },
			$__authorisation_amount_operator_type: "select",
			$__authorisation_amount_operator_values: [
				{
					get name() { return msg("same"); },
					value: "same",
				},
				{
					get name() { return msg("notSame"); },
					value: "not_same",
				},
				{
					name: "=",
					value: "eq",
				},
				{
					name: "!=",
					value: "neq",
				},
				{
					name: ">",
					value: "gt",
				},
				{
					name: "<",
					value: "lt",
				},
				{
					name: ">=",
					value: "gte",
				},
				{
					name: "<=",
					value: "lte",
				},
			],
			$__authorisation_amount_operator_linked: "field",
			$__authorisation_amount_operator_linked_value: "authorisation_amount",
			$__authorisation_amount_operator_optional: true,

			// Authorisation currency
			authorisation_currency: "",
			$__authorisation_currency_type: "select",
			get $__authorisation_currency_placeholder() { return msg("authorisationCurrency"); },
			$__authorisation_currency_values: [
				{
					name: "EUR",
					value: "EUR",
				},
				{
					name: "USD",
					value: "USD",
				},
			],
			$__authorisation_currency_linked: "field",
			$__authorisation_currency_linked_value: "authorisation_currency",
			$__authorisation_currency_optional: true,
			authorisation_currency_operator: "",
			get $__authorisation_currency_operator_label() { return msg("operator"); },
			get $__authorisation_currency_operator_placeholder() { return msg("operator"); },
			$__authorisation_currency_operator_type: "select",
			$__authorisation_currency_operator_values: [
				{
					get name() { return msg("equals"); },
					value: "eq",
				},
				{
					get name() { return msg("notEquals"); },
					value: "neq",
				},
			],
			$__authorisation_currency_operator_linked: "field",
			$__authorisation_currency_operator_linked_value: "authorisation_currency",
			$__authorisation_currency_operator_optional: true,

			// Authorisation ID
			$__authorisation_id_linked: "field",
			$__authorisation_id_linked_value: "authorisation_id",
			$__authorisation_id_optional: true,
			authorisation_id_operator: "same",
			get $__authorisation_id_operator_label() { return msg("operator"); },
			get $__authorisation_id_operator_placeholder() { return msg("operator"); },
			$__authorisation_id_operator_type: "select",
			$__authorisation_id_operator_values: [
				{
					get name() { return msg("same"); },
					value: "same",
				},
				{
					get name() { return msg("notSame"); },
					value: "not_same",
				},
				{
					get name() { return msg("forEach"); },
					value: "each",
				},
			],
			$__authorisation_id_operator_linked: "field",
			$__authorisation_id_operator_linked_value: "authorisation_id",
			$__authorisation_id_operator_optional: true,

			// Chargeback request count
			chargeback_request_count: "",
			$__chargeback_request_count_type: "text",
			get $__chargeback_request_count_placeholder() { return msg("chargebackRequestCount"); },
			$__chargeback_request_count_linked: "field",
			$__chargeback_request_count_linked_value: "chargeback_request_count",
			$__chargeback_request_count_optional: true,
			chargeback_request_count_operator: "",
			get $__chargeback_request_count_operator_label() { return msg("operator"); },
			get $__chargeback_request_count_operator_placeholder() { return msg("operator"); },
			$__chargeback_request_count_operator_type: "select",
			$__chargeback_request_count_operator_values: [
				{
					name: "=",
					value: "eq",
				},
				{
					name: "!=",
					value: "neq",
				},
				{
					name: ">",
					value: "gt",
				},
				{
					name: "<",
					value: "lt",
				},
				{
					name: ">=",
					value: "gte",
				},
				{
					name: "<=",
					value: "lte",
				},
			],
			$__chargeback_request_count_operator_linked: "field",
			$__chargeback_request_count_operator_linked_value: "chargeback_request_count",
			$__chargeback_request_count_operator_optional: true,
		},
	],
	$__rules_decorator: {
		get title() { return msg("processingRules"); },
		blocks: [
			{
				blockless: true,
				order: ["field"],
				gridTemplateColumns: "2fr 1fr",
			},
			{
				blockless: true,
				order: [
					"bin_country_operator",
					"card_type_operator",
					"founding_method_operator",
					"currency_operator",
					"respond_code_operator",
					"transaction_type_operator",
					"acquirer_id_operator",
					"merchant_id_operator",
					"date_operator",
					"time_operator",
					"period_operator",
					"card_entry_mode_operator",
					"pan_operator",
					"authorisation_amount_operator",
					"authorisation_currency_operator",
					"authorisation_id_operator",
					"chargeback_request_count_operator",
					"bin_country",
					"card_type",
					"founding_method",
					"currency",
					"respond_code",
					"transaction_type",
					"acquirer_id",
					"merchant_id",
					"date",
					"time",
					"period",
					"card_entry_mode",
					"pan",
					"authorisation_amount",
					"authorisation_currency",
					"authorisation_id",
					"chargeback_request_count",
				],
				gridTemplateColumns: "2fr 3fr",
				sx: {
					marginTop: "12px",
				},
			},
		],
	},
	$__rules_borders: true,
} as const;
// console.log(">>> ", hydrate(stripMeta(JSON.parse(JSON.stringify(template)))));
// window.console.log(">>> ", stripMeta(JSON.parse(JSON.stringify(template))));
